import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from "@/api/axios";
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API, inr, getStageColor } from "@/App";
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle,
  ArrowRight,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "SENT", label: "Sent" },
  { value: "PAID", label: "Paid" },
  { value: "VOID", label: "Void" },
];

function InvoiceStatusSelect({ stage, project, onStatusChange }) {
  const currentStatus = stage.invoice?.status || "DRAFT";

  return (
    <Select
      value={currentStatus}
      disabled={currentStatus === "PAID"}
      onValueChange={(val) =>
        onStatusChange?.({
          stage_id: stage.stage_id,
          invoice_id: stage.invoice?._id,
          project_id: project?._id,
          newStatus: val,
        })
      }
    >
      <SelectTrigger className="w-[150px]">
        <SelectValue placeholder="Select status" />
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Stage Progress Stepper Component
function StageProgressStepper({ stages, currentStageName, onAdvanceClick }) {
  const sortedStages = [...stages].sort(
    (a, b) => (a.order_idx || 0) - (b.order_idx || 0),
  );
  const currentIndex = sortedStages.findIndex(
    (s) => s.name === currentStageName,
  );
  const isLastStage = currentIndex === sortedStages.length - 1;
  const currentStage = sortedStages[currentIndex];
  const nextStage = sortedStages[currentIndex + 1];

  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-lg border">
      {/* Stepper Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-muted-foreground">
            Project Progress
          </h3>
          <p className="text-lg font-semibold">
            Current Stage:{" "}
            <span className="text-primary">
              {currentStageName || "Not Started"}
            </span>
          </p>
        </div>

        {/* Advance Stage Button */}
        {!isLastStage && currentStage && (
          <Button
            onClick={() => onAdvanceClick(currentStage, nextStage)}
            className="gap-2 bg-green-600 hover:bg-green-700"
          >
            Advance to Next Stage
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
        {isLastStage && (
          <div className="flex items-center gap-2 text-green-600 font-medium">
            <CheckCircle2 className="h-5 w-5" />
            Final Stage
          </div>
        )}
      </div>

      {/* Visual Stage Steps */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {sortedStages.map((stage, idx) => {
          const isCompleted = idx < currentIndex;
          const isCurrent = idx === currentIndex;
          const isFuture = idx > currentIndex;

          return (
            <div key={stage.stage_id} className="flex items-center">
              {/* Stage node */}
              <div
                className={`
                flex items-center gap-2 px-3 py-2 rounded-lg transition-all
                ${isCompleted ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" : ""}
                ${isCurrent ? "bg-primary/10 text-primary ring-2 ring-primary ring-offset-2" : ""}
                ${isFuture ? "bg-muted text-muted-foreground" : ""}
              `}
              >
                {isCompleted ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0" />
                ) : isCurrent ? (
                  <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                    <span className="text-xs text-white font-bold">
                      {idx + 1}
                    </span>
                  </div>
                ) : (
                  <Circle className="h-5 w-5 shrink-0" />
                )}
                <span className="text-sm font-medium whitespace-nowrap">
                  {stage.name}
                </span>
              </div>

              {/* Connector line */}
              {idx < sortedStages.length - 1 && (
                <div
                  className={`w-8 h-0.5 mx-1 ${isCompleted ? "bg-green-400" : "bg-muted-foreground/30"}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function StageInvoiceTable({ mgmt, project, showFinance, refresh }) {
  const [expandedRows, setExpandedRows] = useState({});
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [advanceInfo, setAdvanceInfo] = useState({
    currentStage: null,
    nextStage: null,
  });
  const [isAdvancing, setIsAdvancing] = useState(false);

  const toggleRow = (stageId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [stageId]: !prev[stageId],
    }));
  };

  const handleStatusChange = async ({
    stage_id,
    invoice_id,
    project_id,
    newStatus,
  }) => {
    try {
      console.log("Updating status:", {
        stage_id,
        invoice_id,
        project_id,
        newStatus,
      });

      const res = await api.put(`${API}/invoice/${invoice_id}/status`, {
        status: newStatus,
      });

      if (res.status === 200) {
        refresh();
        alert("Invoice status updated successfully!");
      }
    } catch (err) {
      console.error("Failed to update invoice status:", err);
      alert("Error updating invoice status");
    }
  };

  const handleCreateInvoice = async (stage) => {
    try {
      const res = await api.post(`${API}/invoice`, {
        project_id: mgmt.project.id,
        stage_id: stage.name,
        amount: stage.fee_amount,
        tax: 18,
        notes: "Generated from UI",
      });
      refresh();
    } catch (err) {
      console.error(err);
      alert("Failed to create invoice");
    }
  };

  const openAdvanceDialog = (currentStage, nextStage) => {
    setAdvanceInfo({ currentStage, nextStage });
    setAdvanceDialogOpen(true);
  };

  const confirmAdvanceStage = async () => {
    if (!advanceInfo.currentStage) return;

    setIsAdvancing(true);
    try {
      const res = await api.post(`${API}/invoice/advance-stage`, {
        project_id: mgmt.project.id,
        stage_name: advanceInfo.currentStage.name,
      });
      if (res.status === 200) {
        setAdvanceDialogOpen(false);
        refresh();
      }
    } catch (err) {
      console.error(err);
      alert("Failed to advance stage");
    } finally {
      setIsAdvancing(false);
    }
  };

  const generateInvoicePdf = (project, stage) => {
    const doc = new jsPDF();

    // Page dimensions
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;

    // Colors
    const primaryColor = [41, 128, 185]; // Professional blue
    const darkGray = [52, 73, 94];
    const lightGray = [236, 240, 241];

    // Header Section
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 35, "F");

    // Company/Invoice Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont(undefined, "bold");
    doc.text("INVOICE", margin, 22);

    // Invoice Number (top right)
    doc.setFontSize(12);
    doc.setFont(undefined, "normal");
    doc.text(
      `Invoice #${String(stage.order_idx + 1).padStart(4, "0")}`,
      pageWidth - margin,
      22,
      { align: "right" },
    );

    // Reset colors
    doc.setTextColor(...darkGray);

    // Company Information Section
    let currentY = 50;
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text("Metabolic Office", margin, currentY);

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");

    doc.text("B-4 Olive Heights 1, 17", margin, currentY + 16);

    doc.text(
      "Satellite Rd, Dhanyata Society, Satellite Ahmedabad",
      margin,
      currentY + 24,
    );
    doc.text("Gujarat India 380054", margin, currentY + 32);

    // Invoice Details (top right)
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    doc.text("Invoice Date:", pageWidth - 80, currentY, { align: "left" });
    doc.text("Due Date:", pageWidth - 80, currentY + 8, { align: "left" });

    doc.setFont(undefined, "normal");
    const issueDate = new Date();
    const dueDate = new Date(issueDate);
    dueDate.setDate(dueDate.getDate() + 30); // 30 days from issue

    doc.text(issueDate.toLocaleDateString(), pageWidth - margin, currentY, {
      align: "right",
    });
    doc.text(dueDate.toLocaleDateString(), pageWidth - margin, currentY + 8, {
      align: "right",
    });

    // Bill To Section
    currentY = 100;
    doc.setFillColor(...lightGray);
    doc.rect(margin, currentY, pageWidth - margin * 2, 8, "F");

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("BILL TO:", margin + 5, currentY + 6);

    currentY += 15;
    doc.setFontSize(11);
    doc.setFont(undefined, "normal");
    doc.text(`Client Name:${project?.client || "N/A"}`, margin, currentY);
    doc.text(
      `Phone: ${project?.client_contact || "N/A"}`,
      margin,
      currentY + 8,
    );
    doc.text(
      `Company Name: ${project?.client_company || "N/A"}`,
      margin,
      currentY + 16,
    );
    doc.text(
      `gst details: ${project?.gst_details || "N/A"}`,
      margin,
      currentY + 24,
    );
    // Add client details if available
    // doc.setFont(undefined, "normal");
    // doc.text("Ahmedabad, Gujarat", margin, currentY + 8);

    // Project Information
    currentY += 35;
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(`Project: ${project.name}`, margin, currentY);

    // Items Table
    currentY += 15;

    autoTable(doc, {
      startY: currentY,
      head: [["Description", "Quantity", "Rate", "Amount"]],
      body: [
        [
          stage.name,
          "1",
          `Rs.${stage.fee_amount.toLocaleString()}`,
          `Rs.${stage.fee_amount.toLocaleString()}`,
        ],
      ],
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 11,
        halign: "center",
      },
      bodyStyles: {
        textColor: darkGray,
        fontSize: 10,
      },
      columnStyles: {
        0: { halign: "left", cellWidth: 80 }, // Description
        1: { halign: "center", cellWidth: 25 }, // Quantity
        2: { halign: "right", cellWidth: 35 }, // Rate
        3: { halign: "right", cellWidth: 35 }, // Amount
      },
      margin: { left: margin, right: margin },
      tableWidth: "auto",
      styles: {
        cellPadding: 8,
        lineWidth: 0.1,
        lineColor: [200, 200, 200],
      },
    });

    // Totals Section
    const tableEnd = doc.lastAutoTable.finalY + 10;
    const totalsX = pageWidth - 90;

    // Calculate amounts
    const subtotal = stage.fee_amount;
    const taxRate = 0.18; // 18% GST
    const tax = Math.round(subtotal * taxRate);
    const total = subtotal + tax;

    // Totals box background
    doc.setFillColor(250, 250, 250);
    doc.rect(totalsX - 10, tableEnd, 80, 45, "F");

    // Totals text
    doc.setFontSize(11);
    doc.setFont(undefined, "normal");

    // Subtotal
    doc.text("Subtotal:", totalsX, tableEnd + 10);
    doc.text(
      `Rs.${subtotal.toLocaleString()}`,
      pageWidth - margin,
      tableEnd + 10,
      {
        align: "right",
      },
    );

    // Tax
    doc.text("GST (18%):", totalsX, tableEnd + 20);
    doc.text(`Rs.${tax.toLocaleString()}`, pageWidth - margin, tableEnd + 20, {
      align: "right",
    });

    // Line above total
    doc.setDrawColor(100, 100, 100);
    doc.setLineWidth(0.5);
    doc.line(totalsX, tableEnd + 25, pageWidth - margin, tableEnd + 25);

    // Total
    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    doc.text("TOTAL:", totalsX, tableEnd + 35);
    doc.text(
      `Rs.${total.toLocaleString()}`,
      pageWidth - margin,
      tableEnd + 35,
      {
        align: "right",
      },
    );

    // Payment Terms Section
    const termsY = tableEnd + 60;
    doc.setFillColor(...lightGray);
    doc.rect(margin, termsY, pageWidth - margin * 2, 8, "F");

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("PAYMENT TERMS:", margin + 5, termsY + 6);

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text(
      "Payment is due within 30 days of invoice date.",
      margin,
      termsY + 18,
    );
    doc.text(
      "Late payments may incur additional charges.",
      margin,
      termsY + 26,
    );

    // Footer
    const footerY = doc.internal.pageSize.getHeight() - 25;
    doc.setFillColor(...primaryColor);
    doc.rect(0, footerY, pageWidth, 25, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text("Thank you for your business!", pageWidth / 2, footerY + 10, {
      align: "center",
    });
    doc.text(
      "For questions about this invoice, contact: Metabolic Office",
      pageWidth / 2,
      footerY + 18,
      { align: "center" },
    );

    // Save the PDF
    doc.save(
      `Invoice-${String(stage.order_idx + 1).padStart(
        4,
        "0",
      )}-${project.name.replace(/\s+/g, "-")}.pdf`,
    );
  };

  if (!mgmt) return <div>No data</div>;

  const sortedStages = (mgmt.stages || []).sort(
    (a, b) => (a.order_idx || 0) - (b.order_idx || 0),
  );

  return (
    <>
      {/* Stage Progress Stepper */}
      <StageProgressStepper
        stages={sortedStages}
        currentStageName={project?.current_stage_idx}
        onAdvanceClick={openAdvanceDialog}
      />

      {/* Stage Table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead></TableHead>
            <TableHead>Stage</TableHead>
            <TableHead>Fee</TableHead>
            <TableHead>Hours</TableHead>
            <TableHead>Direct Cost</TableHead>
            <TableHead>Invoice</TableHead>
            <TableHead>Created On</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Paid Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedStages.map((s) => {
            const isExpanded = expandedRows[s.stage_id] || false;
            const hasDelegation = s.delegation && s.delegation.length > 0;
            const isCurrent = s.name === project?.current_stage_idx;

            return (
              <>
                {/* Main row */}
                <TableRow
                  key={s.stage_id}
                  className={`
                    ${hasDelegation ? "cursor-pointer" : ""}
                    ${isCurrent ? "bg-primary/5 border-l-4 border-l-primary" : ""}
                  `}
                  onClick={() => hasDelegation && toggleRow(s.stage_id)}
                >
                  <TableCell className="w-6">
                    {hasDelegation ? (
                      isExpanded ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 rounded-sm text-sm font-medium ${getStageColor(s.name)}`}
                      >
                        {s.name}
                      </span>
                      {isCurrent && (
                        <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                          Current
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {showFinance ? inr.format(s.fee_amount || 0) : "—"}
                  </TableCell>
                  <TableCell>{s.hours || 0}</TableCell>
                  <TableCell>
                    {showFinance ? inr.format(s.direct_cost || 0) : "—"}
                  </TableCell>
                  <TableCell>
                    {!s.invoice ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateInvoice(s);
                        }}
                      >
                        Create Invoice
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          generateInvoicePdf(project, s);
                        }}
                      >
                        Download Invoice
                      </button>
                    )}
                  </TableCell>
                  <TableCell>
                    {!s.invoice ? (
                      <span className="text-gray-500 italic">—</span>
                    ) : (
                      // new Date(s.invoice.createdAt).toLocaleString()
                      new Date(s.invoice.createdAt).toLocaleDateString()
                    )}
                  </TableCell>
                  <TableCell>
                    {!s.invoice ? (
                      <span className="text-gray-500 italic">No Invoice</span>
                    ) : (
                      <InvoiceStatusSelect
                        stage={s}
                        project={project}
                        onStatusChange={handleStatusChange}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {!s.invoice ? (
                      <span className="text-gray-500 italic">—</span>
                    ) : s.invoice.status === "PAID" && s.invoice.paidDate ? (
                      // new Date(s.invoice.paidDate).toLocaleString()
                      new Date(s.invoice.paidDate).toLocaleDateString()
                    ) : (
                      <span className="text-gray-500 italic">—</span>
                    )}
                  </TableCell>
                </TableRow>

                {/* Expanded delegation table */}
                {isExpanded && hasDelegation && (
                  <TableRow>
                    <TableCell colSpan={9} className="bg-gray-50 p-4">
                      <Table className="w-full text-sm border">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Hours</TableHead>
                            <TableHead>Direct Cost</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {s.delegation.map((d, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{d.name}</TableCell>
                              <TableCell>{d.hours}</TableCell>
                              <TableCell>
                                {showFinance
                                  ? inr.format(d.direct_cost || 0)
                                  : "—"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
          })}
        </TableBody>
      </Table>

      {/* Advance Stage Confirmation Dialog */}
      <Dialog open={advanceDialogOpen} onOpenChange={setAdvanceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="h-5 w-5 text-green-600" />
              Advance Project Stage
            </DialogTitle>
            <DialogDescription>
              You're about to progress this project to the next stage.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
              {/* Current Stage */}
              <div className="flex-1 text-center">
                <p className="text-xs text-muted-foreground mb-1">
                  Current Stage
                </p>
                <p className="font-semibold text-lg">
                  {advanceInfo.currentStage?.name}
                </p>
              </div>

              {/* Arrow */}
              <ArrowRight className="h-6 w-6 text-green-600 shrink-0" />

              {/* Next Stage */}
              <div className="flex-1 text-center">
                <p className="text-xs text-muted-foreground mb-1">Next Stage</p>
                <p className="font-semibold text-lg text-green-600">
                  {advanceInfo.nextStage?.name}
                </p>
              </div>
            </div>

            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  <p className="font-medium mb-1">What happens next:</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-700 dark:text-amber-300">
                    <li>
                      The project will move to "{advanceInfo.nextStage?.name}"
                    </li>
                    <li>Team members will see the new current stage</li>
                    <li>This action can be reversed by an admin</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setAdvanceDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmAdvanceStage}
              disabled={isAdvancing}
              className="bg-green-600 hover:bg-green-700"
            >
              {isAdvancing ? "Advancing..." : "Confirm Advance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
